'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Calendar, Clock, Users, PlayCircle, CheckCircle, Zap } from 'lucide-react';

const webinarTopics = [
  'Why you understand English but cannot speak',
  'The 3 biggest mistakes that keep learners stuck',
  'Why grammar alone will never make you fluent',
  'The Immersion Technique — how to build an English environment',
  'How to overcome hesitation and speak with confidence',
  'The 90-day transformation roadmap — live walkthrough',
  'How Fluent English Blueprint works — complete demo',
  'Live Q&A with Coach Ravi',
];

function CountdownTimer({ targetDate }: { targetDate: Date }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculate = () => {
      const diff = targetDate.getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Mins', value: timeLeft.minutes },
    { label: 'Secs', value: timeLeft.seconds },
  ];

  return (
    <div className="flex items-center gap-3">
      {units.map((unit, i) => (
        <React.Fragment key={`countdown-${unit.label}`}>
          <div className="flex flex-col items-center px-3 py-2 rounded-xl bg-white/15 border border-white/20 min-w-[56px]">
            <span className="text-2xl font-extrabold text-white font-tabular leading-none">
              {String(unit.value).padStart(2, '0')}
            </span>
            <span className="text-xs text-white/60 font-medium mt-0.5">{unit.label}</span>
          </div>
          {i < 3 && <span className="text-white/50 font-bold text-lg">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function WebinarSection() {
  const webinarDate = new Date('2026-08-25T14:00:00Z'); // 7:30 PM IST

  return (
    <section className="py-20 lg:py-28 gradient-hero" id="webinar">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div className="flex flex-col gap-8">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <Zap size={14} className="text-primary-light" />
                <span className="text-sm font-semibold text-white">FREE Live Masterclass</span>
              </div>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4">
                Speak Fluent English
                <br />
                <span className="hero-text-gradient">in 90 Days</span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-6">
                A free live masterclass with Coach Ravi. Learn exactly why you can't speak English
                yet — and the proven system to fix it in 90 days.
              </p>
            </div>

            {/* Webinar details */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-white/80">
                <Calendar size={18} className="text-primary-light" />
                <span className="font-semibold">Monday, 25 August 2026</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Clock size={18} className="text-primary-light" />
                <span className="font-semibold">7:30 PM IST · 90 Minutes · Live + Q&A</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Users size={18} className="text-primary-light" />
                <span className="font-semibold">
                  <span className="text-primary-light">847 seats registered</span> · Limited to 1,000
                </span>
              </div>
            </div>

            {/* Countdown */}
            <div>
              <p className="text-sm font-bold text-white/60 uppercase tracking-widest mb-3">
                Starts In
              </p>
              <CountdownTimer targetDate={webinarDate} />
            </div>

            {/* CTA */}
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-base text-white btn-press shadow-glow-green w-full sm:w-auto"
              style={{ background: 'var(--primary)' }}
            >
              <PlayCircle size={22} />
              Reserve My Free Seat
            </Link>

            <p className="text-sm text-white/50">
              ✓ Free Registration · ✓ Replay Available · ✓ No Credit Card Required
            </p>
          </div>

          {/* Right — What you'll learn */}
          <div className="rounded-3xl bg-white/10 border border-white/20 p-6 lg:p-8">
            <p className="text-sm font-bold text-white/60 uppercase tracking-widest mb-5">
              What You'll Learn in This Masterclass
            </p>
            <div className="flex flex-col gap-4">
              {webinarTopics.map((topic, i) => (
                <div key={`topic-${i}`} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={14} className="text-primary-light" />
                  </div>
                  <span className="text-sm font-medium text-white/85 leading-snug">{topic}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-2xl bg-primary/20 border border-primary/30">
              <p className="text-sm font-bold text-white text-center">
                🎁 Bonus: Free speaking assessment for all attendees
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}